import React, { useEffect, useState } from "react";
import { api, fmtNum, fmtDate, errMsg } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { exportToCsv } from "@finance/lib/exportCsv";
import { Button } from "@finance/components/ui/button";
import { toast } from "sonner";
import { DownloadSimple, Warning, Receipt, TrendUp, ArrowsClockwise } from "@phosphor-icons/react";
import { KpiStrip, KpiCard, EmptyState } from "@finance/components/DesignSystem";

export default function MarginAudit() {
  const [data, setData] = useState(null);
  const [flaggedOnly, setFlaggedOnly] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const load = () => api.get("/reports/margin-audit").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const recompute = async () => {
    setRecomputing(true);
    try {
      await api.post("/gl/inventory/rebuild");
      await load();
      toast.success("Margins recomputed from GL COGS");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRecomputing(false); }
  };

  const rows = (data?.rows || []).filter((r) => (flaggedOnly ? r.flagged : true));
  const cols = [
    { key: "lx_po_no", label: "Somcore PO" }, { key: "client_name", label: "Client" },
    { key: "date", label: "Date" }, { key: "currency", label: "Ccy" },
    { key: "revenue", label: "Revenue", format: fmtNum }, { key: "gl_cogs", label: "GL COGS", format: fmtNum },
    { key: "old_displayed_gp", label: "Old Displayed GP", format: fmtNum },
    { key: "correct_gp", label: "Correct GP", format: fmtNum },
    { key: "variance", label: "Overstatement", format: fmtNum },
  ];

  return (
    <Layout>
      <PageHeader
        title="Margin Audit"
        subtitle="Past sales whose displayed Gross Profit diverged from the COGS booked to the General Ledger"
        actions={<>
          <Button variant="outline" data-testid="recompute-margins" disabled={recomputing} onClick={recompute}><ArrowsClockwise size={16} className={`mr-1 ${recomputing ? "animate-spin" : ""}`} />{recomputing ? "Recomputing…" : "Recompute margins"}</Button>
          <Button variant="outline" data-testid="export-margin-audit" onClick={() => exportToCsv("margin-audit", data?.rows || [], cols)}><DownloadSimple size={16} className="mr-1" />Export</Button>
        </>}
      />
      <div className="p-8">
        {!data ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : (
          <>
            <KpiStrip cols={3}>
              <KpiCard icon={Receipt} label="Sales Reviewed" value={data.summary.total_sales} testid="kpi-ma-total" />
              <KpiCard icon={Warning} label="Inflated-Margin Sales" value={data.summary.inflated_count} tone="danger" testid="kpi-ma-inflated" />
              <KpiCard icon={TrendUp} label="Total GP Overstatement" value={`TSh ${fmtNum(data.summary.total_overstatement)}`} tone="accent" testid="kpi-ma-overstatement" />
            </KpiStrip>

            <div className="mb-3 flex items-center gap-2">
              <Button size="sm" variant={flaggedOnly ? "default" : "outline"} data-testid="ma-flagged-only" onClick={() => setFlaggedOnly(true)}>Flagged only</Button>
              <Button size="sm" variant={flaggedOnly ? "outline" : "default"} data-testid="ma-show-all" onClick={() => setFlaggedOnly(false)}>All sales</Button>
            </div>

            {rows.length === 0 ? (
              <EmptyState icon={TrendUp} testid="ma-empty" title="No margin overstatements" message="Every sale's displayed GP already matches the COGS booked to the GL." />
            ) : (
              <div className="overflow-x-auto rounded-sm border border-gray-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
                    <th className="px-4 py-2 text-left">Somcore PO</th><th className="px-4 py-2 text-left">Client</th>
                    <th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-left">Type</th>
                    <th className="px-4 py-2 text-right">Revenue</th><th className="px-4 py-2 text-right">GL COGS</th>
                    <th className="px-4 py-2 text-right">Old GP</th><th className="px-4 py-2 text-right">Correct GP</th>
                    <th className="px-4 py-2 text-right">Overstatement</th>
                  </tr></thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.transaction_id} data-testid={`ma-row-${r.transaction_id}`} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono-num font-medium">{r.lx_po_no || "—"}</td>
                        <td className="px-4 py-2">{r.client_name || "—"}</td>
                        <td className="px-4 py-2 lx-num">{fmtDate(r.date)}</td>
                        <td className="px-4 py-2">{r.is_direct_sale ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Stock Fulfillment</span> : <span className="text-xs text-gray-500">Procurement</span>}</td>
                        <td className="px-4 py-2 text-right lx-num">{fmtNum(r.revenue, "TZS")}</td>
                        <td className="px-4 py-2 text-right lx-num">{fmtNum(r.gl_cogs, "TZS")}</td>
                        <td className="px-4 py-2 text-right lx-num text-gray-500">{fmtNum(r.old_displayed_gp, "TZS")}</td>
                        <td className="px-4 py-2 text-right lx-num font-semibold text-emerald-700">{fmtNum(r.correct_gp, "TZS")}</td>
                        <td className={`px-4 py-2 text-right lx-num font-semibold ${r.variance > 0.01 ? "text-red-600" : "text-gray-400"}`}>{fmtNum(r.variance, "TZS")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
